import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { NAV_LINKS, CONTACT_EMAIL } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. We'll be in touch.");
      setEmail("");
    } catch (err) {
      const msg = err?.response?.status === 409 ? "You're already subscribed." : "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] text-white mt-24">
      <div className="container-cc py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-2 mb-6">
              <span className="w-9 h-9 flex items-center justify-center bg-[#A60201] text-white font-serif-display text-xl">C</span>
              <span className="font-serif-display text-2xl">CraftCulture</span>
            </div>
            <p className="font-serif-display text-3xl md:text-4xl leading-[1.15] max-w-md">
              Let's build a workplace employees love belonging to.
            </p>
            <Link to="/contact" data-testid="footer-contact-cta" className="inline-flex items-center gap-2 mt-8 text-[#FEBE98] border-b border-[#FEBE98] pb-1 hover:gap-3 transition-all">
              Start a conversation <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-white/50 mb-5">Explore</div>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link data-testid={`footer-link-${l.to.replace("/", "") || "home"}`} to={l.to} className="text-sm text-white/80 hover:text-white transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="overline text-white/50 mb-5">Newsletter</div>
            <p className="text-sm text-white/70 mb-4">
              Field notes on culture, engagement and the future of work. Once a month. No fluff.
            </p>
            <form onSubmit={subscribe} className="flex border border-white/20" data-testid="newsletter-form">
              <input
                data-testid="newsletter-email-input"
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent text-sm text-white placeholder:text-white/40 px-4 py-3 outline-none"
              />
              <button data-testid="newsletter-submit-btn" disabled={loading} type="submit" className="bg-[#A60201] text-white px-5 text-sm font-semibold hover:bg-[#7A0101] transition-colors disabled:opacity-60">
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
            <div className="mt-8 text-xs text-white/50 space-y-1">
              <div>{CONTACT_EMAIL}</div>
              <div>India · Singapore · Dubai</div>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-white/50">
          <div>© {new Date().getFullYear()} CraftCulture. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">LinkedIn</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
